import type { Payment, PaymentListItem, PaymentLocation } from '@core/models';

/**
 * Maps a raw {@link Payment} from the API into a flat {@link PaymentListItem}
 * optimised for table rendering.
 *
 * Pure function — no state, no side effects. Extracted from `TransactionStore`
 * so the transform can be unit-tested in isolation and reused wherever a
 * flattened payment row is needed.
 *
 * @param payment - Raw payment object from the API
 * @returns Flattened payment object ready for table display
 */
export function mapPaymentToListItem(payment: Payment): PaymentListItem {
  return {
    id: payment._id,
    type: payment.type?.label || String(payment.type?.value || ''),
    amount: payment.amount,
    statusCode: payment.status?.value ?? 0,
    description: payment.description,
    reference: payment.reference,
    accountNumber: payment.account?.name || 'N/A',
    valueDate: payment.date,
    icon: payment.icon || '',
    title: payment.title,
    status: payment.status?.label ?? '',
    formattedAmount: payment.display_amount,
    date: payment.date,
    balanceBefore: '',
    balanceAfter: payment.balance_after ?? '',
    createdAt: new Date(payment.ordering_date),
    orderingDate: new Date(payment.ordering_date),
    hasAttachments: (payment.payment_references?.length ?? 0) > 0,
    attachmentCount: payment.payment_references?.length ?? 0,
    counterpart: payment.counterpart,
    sentVia: payment.sent_via || '',
    baseAmount: payment.base_amount || '',
    fees: payment.fees ?? '0',
    commission: payment.commission ?? '0',
    accountId: payment.account?.id || '',
    statusIcon: payment.status?.icon ?? '',
    backgroundColor: payment.background_color,
    textColor: payment.text_color,
    // Additional dynamic columns
    category: payment.category || undefined,
    privateNote: payment.private_note || undefined,
    sharedNote: payment.shared_note || undefined,
    tags: payment.tags || undefined,
    paymentReferences:
      payment.payment_references?.map((ref) => ref.ref_number).join(', ') || undefined,
    location: payment.location as PaymentLocation | undefined,
  };
}
